import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import styles from './GoalDetail.module.css';
import { prpHttpAPI } from '../../services/prpHttpAPI';
import PageTracker from "../../components/PageTracker/PageTracker";

const goals = [
  { id: 'education', icon: '🎓', title: 'Child Education' },
  { id: 'marriage', icon: '💍', title: 'Child Marriage' },
  { id: 'home', icon: '🏠', title: 'Home Purchase' },
  { id: 'vehicle', icon: '🚗', title: 'Vehicle' },
  { id: 'vacation', icon: '✈️', title: 'Vacation' },
  { id: 'other', icon: '🎯', title: 'Other Goals' },
];

const DocumentIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
    <line x1="16" y1="13" x2="8" y2="13" />
    <line x1="16" y1="17" x2="8" y2="17" />
    <polyline points="10 9 9 9 8 9" />
  </svg>
);

const PencilIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const formatIndianValue = (val) => {
  if (!val || isNaN(val) || val <= 0) return '₹0';
  if (val >= 10000000) return `₹${(val / 10000000).toFixed(2)} Cr`;
  if (val >= 100000) return `₹${(val / 100000).toFixed(2)} Lakh`;
  return `₹${new Intl.NumberFormat('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(val)}`;
};

const calcHoldingFV = (holding, n, r) => {
  const h = Number(String(holding || 0).replace(/,/g, '')) || 0;
  if (h <= 0) return 0;
  return h * Math.pow(1 + r, n);
};

const calcHoldingReq = (targetFV, n, r) => {
  if (targetFV <= 0) return 0;
  if (n <= 0) return Math.ceil(targetFV);
  return Math.ceil(targetFV / Math.pow(1 + r, n));
};

const calcSipFV = (sip, n, r, ai) => {
  const s = Number(String(sip || 0).replace(/,/g, '')) || 0;
  if (s <= 0 || n <= 0) return 0;
  // Annual compounding for ALL step-up values including 0, so this stays the exact
  // inverse of the Indicative SIP formula and of the cascade's MonthlyInvestmentFV.
  if (Math.abs(r - ai) < 0.000001) {
    return s * 12 * n * Math.pow(1 + r, n - 1);
  }
  return s * 12 * (Math.pow(1 + r, n) - Math.pow(1 + ai, n)) / (r - ai);
};

const calcSipReq = (targetFV, n, r, ai) => {
  if (targetFV <= 0 || n <= 0 || r <= 0) return 0;
  const denom = (Math.abs(r - ai) < 0.000001)
    ? 12 * n * Math.pow(1 + r, n - 1)
    : 12 * (Math.pow(1 + r, n) - Math.pow(1 + ai, n)) / (r - ai);
  return denom > 0 ? Math.ceil(targetFV / denom) : 0;
};

function GoalDetail() {
  const navigate = useNavigate();

  const [activeGoal, setActiveGoal] = useState('education');
  const [loading, setLoading] = useState(false);
  const [savedGoalsList, setSavedGoalsList] = useState([]);
  const [capitalReceipts, setCapitalReceipts] = useState([]);
  const [CRExpReturn, setCRExpReturn] = useState(0.06);
  const [WPreRoR, setWPreRoR] = useState(0.10);
  const [isLoaded, setIsLoaded] = useState(false);
  const [customInflationMap, setCustomInflationMap] = useState({});

  const stepUpCap = React.useMemo(() => {
    try {
      const ud = JSON.parse(localStorage.getItem('userData') || '{}');
      return Math.floor(0.7 * (Number(ud.annual_growth_rate) || 0));
    } catch { return 15; }
  }, []);

  const annualIncreaseCap = React.useMemo(() => {
    try {
      const ud = JSON.parse(localStorage.getItem('userData'));
      const cid = JSON.parse(localStorage.getItem('clientInvestmentData'));
      const val = cid.annual_increase ?? cid.annual_increase_existing_investment;
      if (val !== undefined && val !== null && val !== '' && !isNaN(Number(val))) {
        return Number(val);
      }
      return 15;
    } catch { return 15; }
  }, []);

  const retirementYear = React.useMemo(() => {
    try {
      const ud = JSON.parse(localStorage.getItem('userData') || '{}');
      const currentYear = new Date().getFullYear();
      const userAge = Number(ud.age) || 0;
      const yorVal = Number(ud.year_of_retirement) || 0;
      if (yorVal > 1900) return yorVal;
      if (yorVal > 0 && userAge > 0) return currentYear + (yorVal - userAge);
      return 0;
    } catch { return 0; }
  }, []);

  // ─── Load from localStorage ────────────────────────────────────
  useEffect(() => {
    const raw = localStorage.getItem('clientInvestmentData');
    if (!raw) {
      setIsLoaded(true);
      return;
    }
    try {
      const d = JSON.parse(raw);

      if (d.capital_receipts) setCapitalReceipts(d.capital_receipts);
      if (d.cr_exp_return) setCRExpReturn(Number(d.cr_exp_return) / 100);

      // Compute WPreRoR as holding-weighted average of liquid portfolio returns (including extra_liquid)
      if (d.liquid_weighted_return !== undefined && d.liquid_weighted_return !== null && !isNaN(Number(d.liquid_weighted_return))) {
        setWPreRoR(Number(d.liquid_weighted_return) / 100);
      } else {
        const retPct = {};
        (d.return_percent || []).forEach(r => { retPct[r.asset] = Number(r.return_percent) || 0; });
        const defRet = { mf: 12, stocks: 12, bonds: 8, fdrd: 7, other_liquid: 10 };

        let totalLiqSum = 0;
        let wSum = 0;

        const baseLiq = [
          { asset: 'mf', holding: Number(d.mf_holding) || 0 },
          { asset: 'stocks', holding: Number(d.stocks_holding) || 0 },
          { asset: 'bonds', holding: Number(d.bonds_holding) || 0 },
          { asset: 'fdrd', holding: Number(d.fdrd_holding) || 0 },
          { asset: 'other_liquid', holding: Number(d.other_non_retirement_investment_holding) || 0 },
        ];

        baseLiq.forEach(({ asset, holding }) => {
          if (holding > 0) {
            totalLiqSum += holding;
            const ror = retPct[asset] !== undefined ? retPct[asset] : (defRet[asset] || 0);
            wSum += holding * ror;
          }
        });

        if (Array.isArray(d.extra_liquid)) {
          d.extra_liquid.forEach((item) => {
            const h = Number(item.numericHolding || item.amount || item.holding || 0);
            const ror = Number(item.returnRate || item.return_percent || 0);
            if (h > 0) {
              totalLiqSum += h;
              wSum += h * ror;
            }
          });
        }

        if (totalLiqSum > 0) {
          setWPreRoR(wSum / totalLiqSum / 100);
        } else {
          setWPreRoR(8 / 100);
        }
      }



      if (d.goals?.length > 0) {
        setSavedGoalsList(d.goals.map((g, idx) => {
          const ft = goals.find(t =>
            t.title === g.goal_type || t.id === g.goal_type ||
            t.title.toLowerCase() === (g.goal_type || '').toLowerCase()
          );
          const typeId = ft ? ft.id : 'other';
          const goalType = ft ? ft.title : (g.goal_type || 'Other Goals');
          const isChild = ['education', 'marriage'].includes(typeId);
          return {
            id: Date.now() + idx,
            saved: true,
            typeId,
            GoalType: goalType,
            ChildName: isChild ? (g.child_name || g.ChildName || g.goal_name || '') : '',
            GoalName: !isChild ? (g.goal_name || g.GoalName ||
              (typeId === 'home' ? 'Home Purchase' : typeId === 'vehicle' ? 'Vehicle' : '')) : '',
            GoalYear: g.goal_year ? String(g.goal_year) : '',
            GoalAmtReq: g.GoalAmtReq ? new Intl.NumberFormat('en-IN').format(g.GoalAmtReq)
              : g.amount ? new Intl.NumberFormat('en-IN').format(g.amount) : '',
            ChildAge: g.current_savings_or_age ? String(g.current_savings_or_age) : '',
            expected_inflation: (g.expected_inflation !== undefined && g.expected_inflation !== null && g.expected_inflation !== '') ? String(g.expected_inflation) : '4',
            GrowthRate: g.stepup_sip ? String(g.stepup_sip)
              : g.step_up ? String(g.step_up) : '0',
            LoanFunding: g.loan_amount || g.LoanFunding || '',
            additional_investment: (g.additional_investment || []).map((t, ri) => ({
              id: Date.now() + idx * 100 + ri,
              name: t.name || '',
              CurrentAvailable: t.amount ? String(t.amount) : '',
              SIPAmt: t.sip_amount ? String(t.sip_amount) : '',
            })),
          };
        }));
      }
    } catch (e) { console.error('Error parsing goals', e); }
    setIsLoaded(true);
  }, []);

  useEffect(() => {
    if (!isLoaded) return;

    const raw = localStorage.getItem('clientInvestmentData');
    if (raw) {
      try {
        const d = JSON.parse(raw);
        d.cr_exp_return = Math.round(CRExpReturn * 100);
        d.goals = savedGoalsList.map(g => ({
          goal_type: g.GoalType,
          goal_name: ['education', 'marriage'].includes(g.typeId) ? g.ChildName : g.GoalName,
          child_name: ['education', 'marriage'].includes(g.typeId) ? g.ChildName : '',
          goal_year: Number(g.GoalYear),
          amount: Number(String(g.GoalAmtReq).replace(/,/g, '')),
          current_savings_or_age: Number(g.ChildAge),
          expected_inflation: g.expected_inflation,
          stepup_sip: g.GrowthRate,
          loan_amount: g.LoanFunding ? Number(g.LoanFunding) : 0,
          additional_investment: (g.additional_investment || []).filter(r => r.name).map(r => ({
            name: r.name,
            amount: r.CurrentAvailable ? Number(r.CurrentAvailable) : 0,
            sip_amount: r.SIPAmt ? Number(r.SIPAmt) : 0,
          })),
        }));
        localStorage.setItem('clientInvestmentData', JSON.stringify(d));
      } catch (e) {
        console.error('Error auto-saving goals', e);
      }
    }
  }, [isLoaded, savedGoalsList, CRExpReturn]);

  // ─── Handlers ─────────────────────────────────────────────────
 const handleAddMoreClick = () => {
    const type = goals.find(t => t.id === activeGoal);
    setSavedGoalsList(prev => [{
      id: Date.now(), typeId: activeGoal, GoalType: type.title, icon: type.icon,
      ChildName: '',
      GoalName: activeGoal === 'home' ? 'Home Purchase' : activeGoal === 'vehicle' ? 'Vehicle' : '',
      ChildAge: '', GoalAmtReq: '', GoalYear: '', expected_inflation: '4', GrowthRate: '0',
      LoanFunding: '', saved: false, additional_investment: [],
    }, ...prev]);
  };

  const handleDeleteGoal = (id) =>
    setSavedGoalsList(prev => prev.filter(g => g.id !== id));

  const handleDraftChange = (id, field, value) =>
    setSavedGoalsList(prev => prev.map(g => {
      if (g.id !== id) return g;
      const u = { ...g, [field]: value };
      if (field === 'typeId') u.GoalType = goals.find(t => t.id === value).title;
      return u;
    }));

  const handleGoalNameChange = (id, typeId, value) => {
    const isChild = ['education', 'marriage'].includes(typeId);
    const field = isChild ? 'ChildName' : 'GoalName';
    if (value === '') { handleDraftChange(id, field, ''); return; }
    if (/  /.test(value)) return;
    if (!isChild && /\d/.test(value)) return;
    if (isChild && !/^[a-zA-Z\s]*$/.test(value)) return;
    handleDraftChange(id, field, value);
  };

  const handleSavingsOrAgeChange = (id, value) => {
    const v = value.replace(/\D/g, '');
    if (v === '' || v.length <= 2) handleDraftChange(id, 'ChildAge', v);
  };

  const handleAmountRequired = (id, value) => {
    const v = value.replace(/,/g, '');
    if (v === '') return handleDraftChange(id, 'GoalAmtReq', '');
    if (v.length > 1 && v[0] === '0') return;
    if (!/^\d+$/.test(v)) return;
    if (Number(v) > 100000000) return;
    handleDraftChange(id, 'GoalAmtReq', new Intl.NumberFormat('en-IN').format(Number(v)));
  };

  const handleGoalYearChange = (id, value) => {
    const v = value.replace(/\D/g, '');
    if (v.length <= 4) handleDraftChange(id, 'GoalYear', v);
  };

  const handleEditClick = (id, state) => {
    if (state === true) {
      const g = savedGoalsList.find(g => g.id === id);
      const isChild = ['education', 'marriage'].includes(g.typeId);
      const name = isChild ? g.ChildName : g.GoalName;
      if (!name?.trim() || !g.GoalAmtReq || !g.GoalYear) {
        alert('Please fill all required fields before saving.'); return;
      }
      if (isChild && !g.ChildAge) {
        alert("Please enter Child's Current Age."); return;
      }
      if (Number(g.GrowthRate || 0) > annualIncreaseCap) {
        alert(`Step-up rate cannot exceed Annual Increase in Existing Investments (${annualIncreaseCap}%).`);
        return;
      }
    }
    setSavedGoalsList(prev => prev.map(g => g.id === id ? { ...g, saved: state } : g));
  };

  const handleUpdateTaggedInvestment = (goalId, rowId, field, value, limit) =>
    setSavedGoalsList(prev => prev.map(g => {
      if (g.id !== goalId) return g;
      return {
        ...g,
        additional_investment: g.additional_investment.map(row => {
          if (row.id !== rowId) return row;
          if (field === 'CurrentAvailable' || field === 'SIPAmt') {
            let v = value.replace(/,/g, '');
            if (v === '') return { ...row, [field]: '' };
            if (v.length > 1 && v[0] === '0') return row;
            if (!/^\d+$/.test(v)) return row;
            if (limit != null && Number(v) > limit) v = String(limit);
            if (Number(v) > 100000000) return row;
            return { ...row, [field]: v };
          }
          return { ...row, [field]: value };
        }),
      };
    }));

  const handleAddTaggedInvestment = (goalId) =>
    setSavedGoalsList(prev => prev.map(g =>
      g.id !== goalId ? g : {
        ...g,
        additional_investment: [
          ...(g.additional_investment || []),
          { id: Date.now() + Math.random(), name: '', CurrentAvailable: '', SIPAmt: '' },
        ],
      }
    ));

  const handleDeleteTaggedInvestment = (goalId, rowId) =>
    setSavedGoalsList(prev => prev.map(g =>
      g.id !== goalId ? g : {
        ...g,
        additional_investment: (g.additional_investment || []).filter(r => r.id !== rowId),
      }
    ));

  const handleSelectInvestment = (goalId, rowId, name, holding, sip) =>
    setSavedGoalsList(prev => prev.map(g =>
      g.id !== goalId ? g : {
        ...g,
        additional_investment: g.additional_investment.map(row =>
          row.id === rowId ? { ...row, name, CurrentAvailable: holding, SIPAmt: sip } : row
        ),
      }
    ));

  const getSelectOptions = () => {
    try {
      const d = JSON.parse(localStorage.getItem('clientInvestmentData') || '{}');
      const baseOptions = ['Mutual Funds', 'Stocks', 'Bonds', 'FD/RD',
        d.other_non_retirement_label || 'Other Non Ret.'];
      const options = [...baseOptions];
      if (Array.isArray(d.extra_liquid)) {
        d.extra_liquid.forEach((item, idx) => {
          const label = item.label?.trim() || `Other Non Ret. ${idx + 1}`;
          if (label && !options.includes(label)) {
            options.push(label);
          }
        });
      }
      return options;
    } catch { return ['Mutual Funds', 'Stocks', 'Bonds', 'FD/RD', 'Other Non Ret.']; }
  };

  const handleSaveAndContinue = async () => {
    const userDataObj = JSON.parse(localStorage.getItem('userData'));
    const rmInfo = JSON.parse(localStorage.getItem('logged_rm_info'));
    const isSwatTeam = rmInfo?.BRANCH_NAME.includes('swat') || rmInfo?.BRANCH_NAME.includes('SWAT') || rmInfo?.BRANCH_NAME.includes('VIS - NORTH');

    if (!isSwatTeam && userDataObj.age > 30 && savedGoalsList.length === 0) {
      alert("Please add at least one goal to proceed with plan preparation.");
      return;
    }
    const invalidStepUpGoal = savedGoalsList.find(g => Number(g.GrowthRate || 0) > annualIncreaseCap);
    if (invalidStepUpGoal) {
      const gName = ['education', 'marriage'].includes(invalidStepUpGoal.typeId) ? invalidStepUpGoal.ChildName : invalidStepUpGoal.GoalName;
      alert(`Step-up rate for ${gName ? `"${gName}"` : 'a goal'} cannot exceed Annual Increase in Existing Investments (${annualIncreaseCap}%). Please reduce the step-up rate to proceed.`);
      return;
    }
    setLoading(true);
    const raw = localStorage.getItem('clientInvestmentData');
    if (raw) {
      const d = JSON.parse(raw);
      d.cr_exp_return = Math.round(CRExpReturn * 100);
      d.goals = savedGoalsList.map(g => ({
        goal_type: g.GoalType,
        goal_name: ['education', 'marriage'].includes(g.typeId) ? g.ChildName : g.GoalName,
        child_name: ['education', 'marriage'].includes(g.typeId) ? g.ChildName : '',
        goal_year: Number(g.GoalYear),
        amount: Number(String(g.GoalAmtReq).replace(/,/g, '')),
        current_savings_or_age: Number(g.ChildAge),
        expected_inflation: g.expected_inflation,
        stepup_sip: g.GrowthRate,
        loan_amount: g.LoanFunding ? Number(g.LoanFunding) : 0,
        additional_investment: (g.additional_investment || []).filter(r => r.name).map(r => ({
          name: r.name,
          amount: r.CurrentAvailable ? Number(r.CurrentAvailable) : 0,
          sip_amount: r.SIPAmt ? Number(r.SIPAmt) : 0,
        })),
      }));
      localStorage.setItem('clientInvestmentData', JSON.stringify(d));
      try {
        const saveRes = await prpHttpAPI(d, 'client-investment-details', localStorage.getItem('ssotoken'), 'POST');
        if (!saveRes?.success) {
          alert('Goals could not be saved. Please check your connection and click Prepare Plan again.');
          setLoading(false);
          return;
        }
        window.scrollTo({ top: 0, behavior: 'smooth' });
        navigate('/plan-output', { state: { from: 'GoalDetail' } });
      } catch (err) { console.error('Error saving goals', err.message); }
    }
    setLoading(false);
  };

  // ═══════════════════════════════════════════════════════════════
  //  CASCADE COMPUTATION  (recalculated on every render)
  // ═══════════════════════════════════════════════════════════════
  const goalCascadeData = {};
  const CurrentYear = new Date().getFullYear();

  // Two surplus trackers (spec uses different growth rates):
  //   carriedSurplusCR  — CR-only surplus (Step 3),  grows at CRExpReturn
  //   carriedSurplusInv — investment surplus (Step 7), grows at WPreRoR
  let carriedSurplusCR = 0;
  let carriedSurplusInv = 0;
  let lastGoalYear = 0;

  // carriedSips — spec Second Goal Step 4:
  // SIPs freed after a settled goal continue to compound toward future goals.
  // Each entry: { SIPAmt, AnnualIncrement, PreviousGoalYear }
  let carriedSips = [];

  // ── Step 1: build + sort goals chronologically ─────────────────
  const getPoolsFromStorage = () => {
    try {
      const d = JSON.parse(localStorage.getItem('clientInvestmentData') || '{}');
      const otherLabel = d.other_non_retirement_label || 'Other Non Ret.';
      const holdings = {
        'Mutual Funds': Number(d.mf_holding) || 0,
        'Stocks': Number(d.stocks_holding) || 0,
        'Bonds': Number(d.bonds_holding) || 0,
        'FD/RD': Number(d.fdrd_holding) || 0,
        [otherLabel]: Number(d.other_non_retirement_investment_holding) || 0,
      };
      const sips = {
        'Mutual Funds': Number(d.mf_monthly) || 0,
        'Stocks': Number(d.stocks_monthly) || 0,
        'Bonds': Number(d.bonds_monthly) || 0,
        'FD/RD': Number(d.fdrd_monthly) || 0,
        [otherLabel]: Number(d.other_non_retirement_investment_monthly) || 0,
      };

      if (Array.isArray(d.extra_liquid)) {
        d.extra_liquid.forEach((item, idx) => {
          const label = item.label?.trim() || `Other Non Ret. ${idx + 1}`;
          if (label) {
            const h = item.numericHolding != null ? Number(item.numericHolding)
              : Number(String(item.holding || item.amount || 0).replace(/,/g, '')) || 0;
            const s = item.numericMonthly != null ? Number(item.numericMonthly)
              : Number(String(item.monthly || item.sip_amount || 0).replace(/,/g, '')) || 0;
            holdings[label] = (holdings[label] || 0) + h;
            sips[label] = (sips[label] || 0) + s;
          }
        });
      }

      return { holdings, sips };
    } catch {
      return { holdings: {}, sips: {} };
    }
  };

  const { holdings: remainingHoldingsPool, sips: remainingSIPsPool } = getPoolsFromStorage();

  const goalAvailablePools = {};

  const chronologicalGoals = [...savedGoalsList]
    .map(g => {
      const GoalAmtReq = Number(String(g.GoalAmtReq).replace(/,/g, '')) || 0;
      const LoanFunding = Number(String(g.LoanFunding).replace(/,/g, '')) || 0;
      const GoalYear = Number(g.GoalYear) || 0;
      const n = Math.max(0, GoalYear - CurrentYear);
      const inf = (g.expected_inflation !== undefined && g.expected_inflation !== null && !isNaN(Number(g.expected_inflation))) ? Number(g.expected_inflation) / 100 : 0.05;

      // STEP 1: GoalRequiredValue = (GoalAmtReq − LoanFunding) × (1+expected_inflation)^n
      const GoalRequiredValue = Math.max(0, GoalAmtReq - LoanFunding) * Math.pow(1 + inf, n);
      return { id: g.id, GoalYear, GoalRequiredValue, n, g, originalIndex: savedGoalsList.indexOf(g) };
    })
    .sort((a, b) =>
      a.GoalYear - b.GoalYear ||
      // Same-year goals: allocate CR / carried surplus in CREATION order (first-created
      // wins), independent of saved state. New goals are PREPENDED, so a higher array
      // index means the goal was created earlier → sort by originalIndex DESCENDING.
      // (Using saved-state here caused allocation to flip when the 2nd goal was saved.)
      (b.originalIndex - a.originalIndex)
    )
    .map(cg => {
      const { GoalYear, n } = cg;

      // Snapshot pool available to THIS goal before its own deductions
      goalAvailablePools[cg.id] = {
        holdings: { ...remainingHoldingsPool },
        sips: { ...remainingSIPsPool },
      };

      let goalHoldingSum = 0;
      let goalSipSum = 0;

      // STEP 4: allocate tagged investments (capped at remaining pool)
      const allocatedInvestments = (cg.g.additional_investment || []).map(row => {
        if (!row.name) return { ...row, actualHolding: 0, actualSip: 0 };
        const rawH = Number(String(row.CurrentAvailable).replace(/,/g, '')) || 0;
        const rawS = Number(String(row.SIPAmt).replace(/,/g, '')) || 0;
        const aH = Math.min(rawH, remainingHoldingsPool[row.name] || 0);
        const aS = Math.min(rawS, remainingSIPsPool[row.name] || 0);
        remainingHoldingsPool[row.name] = Math.max(0, (remainingHoldingsPool[row.name] || 0) - aH);
        remainingSIPsPool[row.name] = Math.max(0, (remainingSIPsPool[row.name] || 0) - aS);
        goalHoldingSum += aH;
        goalSipSum += aS;

        return { ...row, actualHolding: aH, actualSip: aS };
      });

      // STEP 4: InvestmentTaggedFV = Σ CurrentAvailable × (1+WPreRoR)^n
      const InvestmentTaggedFV = goalHoldingSum * Math.pow(1 + WPreRoR, GoalYear - CurrentYear);

      // STEP 5: MonthlyInvestmentFV from this goal's tagged SIPs
      //   GrowthRate = 0  → monthly compounding (matches screenshot-verified behaviour)
      //   GrowthRate > 0  → annual step-up formula (per spec)
      const AI = (Number(cg.GrowthRate || cg.g?.GrowthRate) || 0) / 100;
      let MonthlyInvestmentFV = 0;

if (n > 0 && goalSipSum > 0) {
        if (Math.abs(WPreRoR - AI) < 0.000001) {
          // Step-up SIP, edge case WPreRoR ≈ AI (L'Hôpital)
          MonthlyInvestmentFV = goalSipSum * 12 * n * Math.pow(1 + WPreRoR, n - 1);
        } else {
          // Step-up SIP — annual compounding per spec
          // MonthlyFV = SIP×12 × ((1+WPreRoR)^n − (1+AI)^n) / (WPreRoR−AI)
          MonthlyInvestmentFV = goalSipSum * 12 *
            (Math.pow(1 + WPreRoR, n) - Math.pow(1 + AI, n)) / (WPreRoR - AI);
        }
      }
      return { ...cg, InvestmentTaggedFV, MonthlyInvestmentFV, allocatedInvestments };
    });

  // ── Cascade loop ───────────────────────────────────────────────
  // for...of used (not forEach) so that `continue` works correctly
  const crRemaining = (capitalReceipts || []).map(r =>
    Number(String(r.CRAmt || r.amount || 0).replace(/,/g, ''))
  );

  for (const cg of chronologicalGoals) {
    const { GoalYear, GoalRequiredValue, InvestmentTaggedFV } = cg;
    const goalReceipts = [];
    let remainingToSettle = GoalRequiredValue;

    // Snapshot available capital receipts at this GoalYear BEFORE this goal consumes any
    const crAvailableAtGoal = (capitalReceipts || []).map((r, i) => {
      const CRYear = Number(r.CRYear || r.year || r.receiptYear || r.receipt_year || 0);
      const remPrincipal = crRemaining[i] || 0;
      if (GoalYear >= CRYear && CRYear > 0 && remPrincipal > 0) {
        const yrs = GoalYear - CRYear;
        const fv = remPrincipal * Math.pow(1 + CRExpReturn, yrs);
        return {
          CRName: r.CRName || r.description || 'Unnamed Receipt',
          CRYear,
          remPrincipal,
          fv,
          rateLabel: `${Math.round(CRExpReturn * 100)}% (Conservative Rate)`,
        };
      }
      return null;
    }).filter(Boolean);

    const directCRFV = crAvailableAtGoal.reduce((sum, item) => sum + item.fv, 0);

    // ── Calculate available cascaded CR surplus at GoalYear ─────
    let compoundedCRSurplus = 0;
    if (carriedSurplusCR > 0 && lastGoalYear > 0 && GoalYear >= lastGoalYear) {
      compoundedCRSurplus = carriedSurplusCR * Math.pow(1 + CRExpReturn, GoalYear - lastGoalYear);
    }

    // ── Total Goal-Year Capital Receipts Available ──────────────
    // Sum of capital receipt of current year and cascaded capital receipt of previous goal
    const totalGoalYearCR = directCRFV + compoundedCRSurplus;

    // ── Calculate available cascaded liquid surplus at GoalYear ───
    // Sum of cascaded investment surplus and freed SIP surplus from previous goals
    let compoundedInvSurplus = 0;
    if (carriedSurplusInv > 0 && lastGoalYear > 0 && GoalYear >= lastGoalYear) {
      compoundedInvSurplus = carriedSurplusInv * Math.pow(1 + WPreRoR, GoalYear - lastGoalYear);
    }

      // Credit this goal with the FV of the running base grown over the gap
    // since the last goal (the base already reflects everything folded in
    // via step 9 below, so no per-entry reset/double-count is needed).
    let availableCarriedSipFV = 0;
    if (carriedSips.length > 0 && GoalYear > 0) {
      carriedSips.forEach(sip => {
        if (GoalYear <= sip.PreviousGoalYear) return;
        const yearsGap = GoalYear - sip.PreviousGoalYear;
        const yearsToPrev = sip.PreviousGoalYear - CurrentYear;
        const sipStart = sip.SIPAmt * Math.pow(1 + sip.AI, yearsToPrev);
        if (Math.abs(WPreRoR - sip.AI) < 0.000001) {
          availableCarriedSipFV += sipStart * 12 * ((Math.pow(1 + WPreRoR, yearsGap) - 1) / WPreRoR);
        } else {
          availableCarriedSipFV += sipStart * 12 *
            (Math.pow(1 + WPreRoR, yearsGap) - Math.pow(1 + sip.AI, yearsGap)) /
            (WPreRoR - sip.AI);
        }
      });
    }

    const cascadedLiquidSurplus = compoundedInvSurplus + availableCarriedSipFV;

    let mappedToGoal = 0;

    // ── 1. Carry CR surplus from previous goal ──────────────────
    if (compoundedCRSurplus > 0 && remainingToSettle > 0) {
      const yrs = GoalYear - lastGoalYear;
      const apply = Math.min(compoundedCRSurplus, remainingToSettle);
      const principal = apply / Math.pow(1 + CRExpReturn, yrs);
      mappedToGoal += apply;
      remainingToSettle -= apply;
      carriedSurplusCR -= principal;
      goalReceipts.push({
        CRName: 'CR surplus from previous goal', CRYear: lastGoalYear,
        yearsGrew: yrs, CRAmt: principal, futureVal: apply,
        isSurplusCarry: true,
        rateLabel: `${Math.round(CRExpReturn * 100)}% (Conservative Rate)`,
      });
    }

    // ── 2. Carry investment surplus from previous goal ──────────
    if (compoundedInvSurplus > 0 && remainingToSettle > 0) {
      const yrs = GoalYear - lastGoalYear;
      const apply = Math.min(compoundedInvSurplus, remainingToSettle);
      const principal = apply / Math.pow(1 + WPreRoR, yrs);
      mappedToGoal += apply;
      remainingToSettle -= apply;
      carriedSurplusInv -= principal;
      goalReceipts.push({
        CRName: 'Investment surplus from previous goal', CRYear: lastGoalYear,
        yearsGrew: yrs, CRAmt: principal, futureVal: apply,
        isSurplusCarry: true,
        rateLabel: `${Math.round(WPreRoR * 100)}% (portfolio rate)`,
      });
    }

    // ── 3. NOW apply Capital Receipts only on remaining gap ──────
    if (capitalReceipts?.length > 0 && GoalYear > 0) {
      for (let i = 0; i < capitalReceipts.length; i++) {
        if (remainingToSettle <= 0.01) break;
        if ((crRemaining[i] || 0) <= 0.01) continue;
        const r = capitalReceipts[i];
        const CRYear = Number(r.CRYear || r.year || r.receiptYear || r.receipt_year || 0);
        const CRName = r.CRName || r.description || 'Unnamed Receipt';
        if (GoalYear >= CRYear) {
          const yrs = Math.max(0, GoalYear - CRYear);
          const growthFactor = Math.pow(1 + CRExpReturn, yrs);
          const maxFV = crRemaining[i] * growthFactor;
          const crFV = Math.min(maxFV, remainingToSettle);
          const principal = crFV / growthFactor;
          mappedToGoal += crFV;
          remainingToSettle -= crFV;
          crRemaining[i] -= principal;
          goalReceipts.push({
            CRName, CRYear, yearsGrew: yrs, CRAmt: principal, futureVal: crFV,
            isSurplusCarry: false,
            rateLabel: `${Math.round(CRExpReturn * 100)}% (Conservative Rate)`,
          });
        }
      }
    }

    // ── 4. Carried SIPs from previous goals ─────────────────────
      if (availableCarriedSipFV > 0 && GoalYear > 0) {
      const apply = Math.min(availableCarriedSipFV, remainingToSettle);
      mappedToGoal += apply;
      remainingToSettle -= apply;
      carriedSips.forEach(sip => {
        if (GoalYear > sip.PreviousGoalYear) {
          sip.PreviousGoalYear = GoalYear;
        }
      });
    }

    // ── 5. Then deduct tagged holding FV (only what's needed) ────
    const holdingFVUsed = Math.max(0, Math.min(InvestmentTaggedFV, remainingToSettle));
    remainingToSettle = Math.max(0, remainingToSettle - InvestmentTaggedFV);

    // ── 6. Then deduct tagged SIP FV (only what's needed) ────────
    const sipFVUsed = Math.max(0, Math.min(cg.MonthlyInvestmentFV, remainingToSettle));
    remainingToSettle = Math.max(0, remainingToSettle - cg.MonthlyInvestmentFV);

    // ── 7. Final totals ──────────────────────────────────────────
    const TotalAvailable = holdingFVUsed + sipFVUsed + mappedToGoal;
    const GoalNetReq = Math.max(0, Math.round(GoalRequiredValue) - Math.round(TotalAvailable));
    const newSurplus = Math.max(0, TotalAvailable - GoalRequiredValue);

    // ── 8. Update surplus pools ──────────────────────────────────
    if (GoalNetReq > 0) {
      carriedSurplusCR = 0;
      carriedSurplusInv = 0;
    } else {
      if (carriedSurplusInv > 0 && lastGoalYear > 0 && GoalYear > lastGoalYear) {
        carriedSurplusInv *= Math.pow(1 + WPreRoR, GoalYear - lastGoalYear);
      }
      carriedSurplusInv += newSurplus;
    }

    // ── 9. Register freed SIPs for future goals ──────────────────
     const currentAI = (Number(cg.g.GrowthRate) || 0) / 100;
    (cg.allocatedInvestments || []).forEach(row => {
      if ((row.actualSip || 0) > 0) {
        carriedSips.push({
          SIPAmt: row.actualSip, AI: currentAI, PreviousGoalYear: GoalYear,
        });
      }
    });

    if (GoalYear > lastGoalYear) {
      lastGoalYear = GoalYear;
    }

    goalCascadeData[cg.id] = {
      GoalRequiredValue,
      InvestmentTaggedFV: holdingFVUsed,
      MonthlyInvestmentFV: sipFVUsed,
      GoalNetReq,
      goalReceipts,
      totalGoalYearCR,
      crAvailableAtGoal,
      compoundedCRSurplus,
      cascadedLiquidSurplus,
      mappedToGoal,
      surplus: newSurplus,
    };


  }
  // end for...of


  // ═══════════════════════════════════════════════════════════════
  //  RENDER
  // ═══════════════════════════════════════════════════════════════
  return (
    <div className={styles.container}>
      <PageTracker currentStep={4} />
      <div className={styles.mainCard}>

        <div className={styles.header}>
          <div className={styles.headerTitle}>Goals Configuration</div>
        </div>

        {/* ADD GOAL */}
        <div className={styles.addGoalSection}>
          <label className={styles.addGoalLabel}>ADD A GOAL</label>
          <div className={styles.addGoalControls}>
            <select className={styles.typeSelect} value={activeGoal}
              onChange={e => setActiveGoal(e.target.value)}>
              {goals.map(t => <option key={t.id} value={t.id}>{t.icon} {t.title}</option>)}
            </select>
            <button className={styles.addGoalBtn} onClick={handleAddMoreClick}>
              + Add a Goal
            </button>
          </div>
        </div>

        {/* GOAL LIST */}
        {savedGoalsList.map(goal => {
          const GoalAmtReq = Number(String(goal.GoalAmtReq).replace(/,/g, '')) || 0;
          const GoalYear = Number(goal.GoalYear) || 0;
          const currentYear = new Date().getFullYear();
          const n = Math.max(0, GoalYear - currentYear);
          const GoalExpRet = WPreRoR;

          const cascade = goalCascadeData[goal.id] || {
            GoalRequiredValue: 0, InvestmentTaggedFV: 0, MonthlyInvestmentFV: 0,
            GoalNetReq: 0, goalReceipts: [], surplus: 0,
            totalGoalYearCR: 0, crAvailableAtGoal: [], cascadedLiquidSurplus: 0, mappedToGoal: 0,
          };

          const {
            GoalRequiredValue, GoalNetReq, goalReceipts,
            totalGoalYearCR = 0,
            crAvailableAtGoal = [],
            cascadedLiquidSurplus = 0, mappedToGoal = 0,
          } = cascade;
          const InvestmentTaggedFV = cascade.InvestmentTaggedFV || 0;
          const MonthlyInvestmentFV = cascade.MonthlyInvestmentFV || 0;

          // Compute actual Capital Receipts value applied (excluding carried surpluses) from existing goalReceipts array
          const actualCRFV = (goalReceipts || [])
            .filter(cr => !cr.isSurplusCarry)
            .reduce((sum, cr) => sum + (cr.futureVal || 0), 0);

          let crLabelExtra = '';
          if (crAvailableAtGoal.length > 0) {
            crLabelExtra = crAvailableAtGoal.map(item => {
              const amtStr = item.remPrincipal > 0 ? ` of ${formatIndianValue(item.remPrincipal)}` : '';
              return `${item.CRName} (${item.CRYear})${amtStr} — grew at ${item.rateLabel} to goal year`;
            }).join(', ');
            if (cascade.compoundedCRSurplus > 0) {
              crLabelExtra += ' + CR surplus from previous goal';
            }
          } else {
            const crItems = (goalReceipts || []).filter(cr => !cr.isSurplusCarry);
            if (crItems.length > 0) {
              crLabelExtra = crItems.map(cr => {
                const amtStr = cr.CRAmt ? ` of ${formatIndianValue(cr.CRAmt)}` : '';
                return `${cr.CRName} (${cr.CRYear})${amtStr} — grew at ${cr.rateLabel} to goal year`;
              }).join(', ');
            }
          }


         // ── Indicative SIP ────────────────────────────────────
          // Matches PlanOutput.jsx formula exactly:
          const AI_goal = (Number(goal.GrowthRate) || 0) / 100;
          let indicativeSip = 0;
          if (n > 0 && GoalExpRet > 0 && GoalNetReq > 0) {
            if (Math.abs(GoalExpRet - AI_goal) < 0.000001) {
              // Edge case: WPreRoR ≈ AI (L'Hôpital)
              const denom = n * Math.pow(1 + GoalExpRet, n - 1);
              indicativeSip = denom > 0 ? GoalNetReq / denom : 0;
            } else {
              // Sinking fund formula
              const denom = (Math.pow(1 + GoalExpRet, n) - Math.pow(1 + AI_goal, n)) / (GoalExpRet - AI_goal);
              indicativeSip = denom > 0 ? GoalNetReq / denom : 0;
            }
            indicativeSip = indicativeSip / 12;
          }

          // ── Indicative Lumpsum ────────────────────────────────
          // Step 3.1: Lumpsum = GoalNetReq / (1+WPreRoR)^n
          const indicativeLumpsum = n > 0
            ? Math.max(0, GoalNetReq / Math.pow(1 + GoalExpRet, n))
            : 0;

          // ── Stable gross budgets (independent of this goal's own holding/SIP) ──
          // selfFundNeedFV = the FV this goal's own holding + SIP must cover =
          // GoalRequiredValue minus CR / carried surplus / carried SIP (all computed
          // BEFORE this goal's holding/SIP in the cascade). It does not change as the
          // user edits the holding/SIP fields, so the caps never collapse to 0 when a
          // field is cleared — the user can always retype up to the budget.
          const selfFundNeedFV = Math.max(0, GoalRequiredValue - mappedToGoal);
          const lumpsumHoldingBudget = n > 0
            ? Math.round(selfFundNeedFV / Math.pow(1 + GoalExpRet, n))
            : Math.round(selfFundNeedFV);
          const indicativeSipBudget = (n > 0 && GoalExpRet > 0 && selfFundNeedFV > 0)
            ? calcSipReq(selfFundNeedFV, n, GoalExpRet, AI_goal)
            : 0;

          return (
            <div key={goal.id} className={styles.goalItem}>

              {/* Header */}
              <div className={styles.goalItemHeader}>
                <div className={styles.goalItemTitle}>
                  {goals.find(t => t.id === goal.typeId)?.icon} {goal.GoalType}
                </div>
                <div className={styles.goalItemActions}>
                  {GoalNetReq > 0 && (
                    <span className={styles.shortfallBadge}>Shortfall expected</span>
                  )}
                  {goal.saved ? (
                    <div className={styles.savedStatus}>
                      <span className={styles.savedText}>✓ Saved</span>
                      <button className={styles.editBtn}
                        onClick={() => handleEditClick(goal.id, false)}>
                        <PencilIcon /> Edit
                      </button>
                    </div>
                  ) : (
                    <>
                      <span className={styles.enterAmount}>Enter amount</span>
                      <button className={styles.closeBtn}
                        onClick={() => handleDeleteGoal(goal.id)}>✕</button>
                    </>
                  )}
                </div>
              </div>

              {/* Form fields */}
              <div className={
                ['education', 'marriage'].includes(goal.typeId) ? styles.formGrid : styles.formGrid3
              }>
                <div className={styles.formGroup}>
                  <label className={styles.label}>
                    {['education', 'marriage'].includes(goal.typeId) ? 'Child Name'
                      : ['home', 'vehicle'].includes(goal.typeId) ? 'Goal Name'
                        : 'Description'}
                  </label>
                  <input type="text" className={styles.input} disabled={goal.saved}
                    placeholder={
                      ['education', 'marriage'].includes(goal.typeId) ? 'e.g. Sarah'
                        : goal.typeId === 'home' ? 'e.g. 3BHK Apartment'
                          : goal.typeId === 'vehicle' ? 'e.g. SUV'
                            : goal.typeId === 'vacation' ? 'e.g. Europe Trip'
                              : 'e.g. Business Venture'
                    }
                    value={['education', 'marriage'].includes(goal.typeId) ? goal.ChildName : goal.GoalName}
                    onChange={e => handleGoalNameChange(goal.id, goal.typeId, e.target.value)}
                  />
                </div>

                {['education', 'marriage'].includes(goal.typeId) && (
                  <div className={styles.formGroup}>
                    <label className={styles.label}>Child's Current Age</label>
                    <input type="text" className={styles.input} disabled={goal.saved}
                      placeholder="e.g. 5" value={goal.ChildAge}
                      onChange={e => handleSavingsOrAgeChange(goal.id, e.target.value)} />
                  </div>
                )}

                <div className={styles.formGroup}>
                  <label className={`${styles.label} ${styles.required}`}>
                    Amount Required Today (₹)
                  </label>
                  <input type="text" className={styles.input} disabled={goal.saved}
                    placeholder="e.g. 50,00,000" value={goal.GoalAmtReq}
                    onChange={e => handleAmountRequired(goal.id, e.target.value)} />
                  {GoalAmtReq > 0 && GoalYear > currentYear && (
                    <div className={styles.futureValueBadge}>
                      Value at Goal Year ({GoalYear}):&nbsp;
                      <strong>{formatIndianValue(GoalRequiredValue)}</strong>
                      &nbsp;@ {goal.expected_inflation || 5}% infl.
                    </div>
                  )}
                </div>
              </div>

              {/* Loan planning (home / vehicle) */}
              {['home', 'vehicle'].includes(goal.typeId) && (
                <div className={styles.loanPlanningSection}>
                  <div className={styles.loanPlanningHeader}>
                    <span className={styles.loanIcon}>🏛️</span>
                    <span className={styles.loanTitle}>Loan Planning</span>
                  </div>
                  <div className={styles.loanPlanningInputGroup}>
                    <label className={styles.loanLabel}>Loan Amount Willing to Take (₹)</label>
                    <input type="text" className={styles.loanInput} disabled={goal.saved}
                      placeholder="e.g. 50,00,000"
                      value={goal.LoanFunding
                        ? new Intl.NumberFormat('en-IN').format(Number(goal.LoanFunding)) : ''}
                      onChange={e => {
                        const v = e.target.value.replace(/,/g, '');
                        if (v === '') { handleDraftChange(goal.id, 'LoanFunding', ''); return; }
                        if (v.length > 1 && v[0] === '0') return;
                        if (!/^\d+$/.test(v) || Number(v) > 100000000) return;
                        handleDraftChange(goal.id, 'LoanFunding', v);
                      }} />
                  </div>
                  {GoalAmtReq > 0 && (
                    <div className={styles.loanPlanningSummaryCard}>
                      <div className={styles.loanPlanningSummaryTitle}>
                        Full Target Corpus ({GoalYear}): {formatIndianValue(GoalRequiredValue)}
                      </div>
                      <div className={styles.loanPlanningSummarySub}>
                        Bank Loan: {formatIndianValue(Number(goal.LoanFunding) || 0)} | Self-arrange:&nbsp;
                        {formatIndianValue(Math.max(0, GoalAmtReq - (Number(goal.LoanFunding) || 0)))} today
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Goal Year + Inflation */}
              <div className={styles.secondaryParams}>
                <div className={styles.formGroup}>
                  <label className={`${styles.label} ${styles.required}`}>Goal Year</label>
                  <input type="text" className={styles.input} disabled={goal.saved}
                    placeholder="2027" value={goal.GoalYear}
                    onChange={e => handleGoalYearChange(goal.id, e.target.value)} />
                </div>
                <div className={styles.formGroup}>
                  <label className={styles.label}>Inflation for this Goal</label>
                  {customInflationMap[goal.id] ? (
                    <div style={{ position: 'relative', width: '100%' }}>
                      <input
                        type="text"
                        className={styles.input}
                        style={{ paddingRight: '28px', fontSize: '0.85rem' }}
                        placeholder="0.0"
                        value={goal.expected_inflation}
                        disabled={goal.saved}
                        autoFocus
                        onChange={e => {
                          let valStr = e.target.value;
                          if (valStr === "") {
                            handleDraftChange(goal.id, 'expected_inflation', "");
                            return;
                          }
                          if (/^\d*\.?\d*$/.test(valStr)) {
                            const numVal = parseFloat(valStr);
                            if (!isNaN(numVal) && numVal > 15) {
                              handleDraftChange(goal.id, 'expected_inflation', "15");
                            } else {
                              handleDraftChange(goal.id, 'expected_inflation', valStr);
                            }
                          }
                        }}
                        onKeyDown={e => {
                          if (e.key === 'Enter') {
                            e.target.blur();
                          }
                        }}
                        onBlur={e => {
                          setCustomInflationMap(prev => ({ ...prev, [goal.id]: false }));
                          let val = e.target.value;
                          if (val === "" || isNaN(parseFloat(val))) {
                            handleDraftChange(goal.id, 'expected_inflation', "4");
                          } else {
                            let numVal = parseFloat(val);
                            if (numVal < 0) numVal = 0;
                            if (numVal > 15) numVal = 15;
                            handleDraftChange(goal.id, 'expected_inflation', String(numVal));
                          }
                        }}
                      />
                      <span style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        color: '#2e7d32',
                        fontWeight: '700',
                        fontSize: '0.85rem',
                        pointerEvents: 'none'
                      }}>%</span>
                    </div>
                  ) : (
                    <select
                      className={styles.select}
                      disabled={goal.saved}
                      value={goal.expected_inflation !== undefined && goal.expected_inflation !== null && goal.expected_inflation !== '' ? String(goal.expected_inflation) : '4'}
                      onChange={e => {
                        if (e.target.value === 'custom') {
                          setCustomInflationMap(prev => ({ ...prev, [goal.id]: true }));
                          handleDraftChange(goal.id, 'expected_inflation', '');
                        } else {
                          handleDraftChange(goal.id, 'expected_inflation', e.target.value);
                        }
                      }}
                    >
                      <option value="4">4%</option>
                      <option value="5">5%</option>
                      <option value="6">6%</option>
                      <option value="7">7%</option>
                      <option value="8">8%</option>
                      <option value="9">9%</option>
                      <option value="10">10%</option>
                      <option value="11">11%</option>
                      <option value="12">12%</option>
                      <option value="13">13%</option>
                      <option value="14">14%</option>
                      <option value="15">15%</option>
                      {![4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15].includes(Number(goal.expected_inflation)) && goal.expected_inflation !== '' && goal.expected_inflation !== undefined && goal.expected_inflation !== null && (
                        <option value={String(goal.expected_inflation)}>{goal.expected_inflation}%</option>
                      )}
                      <option value="custom">Custom</option>
                    </select>
                  )}
                </div>
              </div>

              {(() => {
                const GoalYearNum = Number(goal.GoalYear) || 0;
                const isAfterRetirement = GoalYearNum > 0 && retirementYear > 0 && GoalYearNum > retirementYear;

                if (isAfterRetirement) {
                  return (
                    <>
                      <div className={styles.additionalRequiredBox}>
                        ℹ️&nbsp;<span style={{ marginLeft: '4px' }}>
                      <b> As this goal is scheduled to occur after retirement, it is incorporated directly into the Retirement corpus target, hence no need to allocate liquid assets </b>
                        </span>
                      </div>

                    </>
                  );
                }

                return (
                  <>
                    {/* Capital receipts applied */}
                    {(goalReceipts.length > 0 || totalGoalYearCR > 0 || mappedToGoal > 0 || (capitalReceipts && capitalReceipts.length > 0)) && (
                      <div className={styles.capitalReceiptBox}>
                        {/* <div className={styles.capitalReceiptTitle}>
                          💰 Capital Receipt Auto-Applied
                        </div> */}
                        <div className={styles.capitalReceiptRow}>
                          <span className={styles.capitalReceiptRowName}>
                            {crLabelExtra ? ` ${crLabelExtra}` : 'Goal-year capital receipts:'}
                          </span>
                          <span className={styles.capitalReceiptRowVal}>
                            {formatIndianValue(totalGoalYearCR)}
                          </span>
                        </div>
                        <div className={styles.capitalReceiptRow}>
                          <span className={styles.capitalReceiptRowName}>
                            Cascaded liquid surplus:
                          </span>
                          <span className={styles.capitalReceiptRowVal}>
                            {cascadedLiquidSurplus > 0 ? formatIndianValue(cascadedLiquidSurplus) : '₹0'}
                          </span>
                        </div>
                        <div className={styles.capitalReceiptTotalRow}>
                          <span>Mapped to goal:</span>
                          <span>
                            {formatIndianValue(mappedToGoal)}
                          </span>
                        </div>
                      </div>
                    )}

              {/* Funded vs shortfall UI */}
              {GoalNetReq === 0 && GoalRequiredValue > 0 ? (
                <>
                  {(() => {
                    const cascadedSurplusFV = Math.max(0, mappedToGoal - actualCRFV);
                    const hasInvestments = (InvestmentTaggedFV + MonthlyInvestmentFV) > 0;
                    return (
                      <div className={styles.goalFullyFundedBox}>
                        &#9745;&#160;<span>
                          <strong>Goal Fully Funded</strong> &mdash;&nbsp;
                          {hasInvestments && (
                            <>Investments FV (<strong>{formatIndianValue(InvestmentTaggedFV + MonthlyInvestmentFV)}</strong>)</>
                          )}
                          {actualCRFV > 0 && (
                            <>{hasInvestments ? ' + ' : ''}Capital Receipts (<strong>{formatIndianValue(actualCRFV)}</strong>)</>
                          )}
                          {cascadedSurplusFV > 0 && (
                            <>{(hasInvestments || actualCRFV > 0) ? ' + ' : ''}Cascaded Surplus (<strong>{formatIndianValue(cascadedSurplusFV)}</strong>)</>
                          )}
                          &nbsp;cover the target of&nbsp;
                          <strong>{formatIndianValue(GoalRequiredValue)}</strong>.
                          No additional SIP needed.
                        </span>
                      </div>
                    );
                  })()}
                  {goal.saved ? (
                    // ── SAVED: simple read-only text ──
                    <div style={{ padding: '4px 0 8px', fontSize: '0.83rem', color: '#555' }}>
                      {(goal.additional_investment || []).filter(r => r.name).length > 0
                        ? (goal.additional_investment || []).filter(r => r.name).map((row, idx) => (
                          <span key={idx}>
                            {row.name}: ₹{Number(row.CurrentAvailable || 0).toLocaleString('en-IN')}
                            &nbsp;— <em>click Edit to modify.</em>
                          </span>
                        ))
                        : <span style={{ color: '#888' }}>No investments tagged — click Edit to modify.</span>
                      }
                    </div>
                  ) : (
                    // ── UNSAVED: full editable section ──
                    <div className={styles.fundedTaggedSection}>
                      <div className={styles.fundedTaggedTitle}>INVESTMENTS TAGGED (GOAL ALREADY FUNDED)</div>
                      <div className={styles.fundedTaggedHeaderRow}>
                        <span>INVESTMENT (LIQUID)</span>
                        <span>ALLOCATED HOLDING (₹)</span>
                        <span>SIP / MO (₹)</span>
                      </div>

                      {(goal.additional_investment?.length > 0
                        ? goal.additional_investment
                        : [{ id: 'funded-empty', name: '', CurrentAvailable: '', SIPAmt: '' }]
                      ).map(row => {
                        const isInit = row.id === 'funded-empty';
                        const pool = goalAvailablePools[goal.id] || { holdings: {}, sips: {} };
                        const AI_goal = (Number(goal.GrowthRate) || 0) / 100;

                        // Net FV requirement to be self-funded for this goal (excluding CR / carried surplus)
                        const selfFundNeedFV = Math.max(0, GoalRequiredValue - mappedToGoal);

                        // Pool availability limits for row.name
                        const otherH_asset = (goal.additional_investment || [])
                          .filter(r => r.id !== row.id && r.name === row.name)
                          .reduce((sum, r) => sum + (Number(String(r.CurrentAvailable).replace(/,/g, '')) || 0), 0);
                        const otherS_asset = (goal.additional_investment || [])
                          .filter(r => r.id !== row.id && r.name === row.name)
                          .reduce((sum, r) => sum + (Number(String(r.SIPAmt).replace(/,/g, '')) || 0), 0);

                        const limH_pool = row.name ? Math.max(0, (pool.holdings[row.name] || 0) - otherH_asset) : 0;
                        const limS_pool = row.name ? Math.max(0, (pool.sips[row.name] || 0) - otherS_asset) : 0;

                        // Other rows' holdings FV and SIPs FV
                        const otherRowsHoldingFV = (goal.additional_investment || [])
                          .filter(r => r.id !== row.id)
                          .reduce((sum, r) => sum + calcHoldingFV(r.CurrentAvailable, n, GoalExpRet), 0);
                        const otherRowsSipFV = (goal.additional_investment || [])
                          .filter(r => r.id !== row.id)
                          .reduce((sum, r) => sum + calcSipFV(r.SIPAmt, n, GoalExpRet, AI_goal), 0);

                        // Current row's entered values
                        const currHoldingVal = Number(String(row.CurrentAvailable || 0).replace(/,/g, '')) || 0;
                        const currSipVal = Number(String(row.SIPAmt || 0).replace(/,/g, '')) || 0;

                        const currHoldingFV = calcHoldingFV(currHoldingVal, n, GoalExpRet);
                        const currSipFV = calcSipFV(currSipVal, n, GoalExpRet, AI_goal);

                        // 1. Max allowed Lumpsum for this row (given current SIP in this row + other rows)
                        const remTargetForLumpsumFV = Math.max(0, selfFundNeedFV - currSipFV - otherRowsSipFV - otherRowsHoldingFV);
                        const lumpsumCapForRow = calcHoldingReq(remTargetForLumpsumFV, n, GoalExpRet);
                        const effHoldingLimit = Math.min(limH_pool, lumpsumCapForRow);
                        const lumpsumIsBinding = lumpsumCapForRow <= limH_pool;

                        // 2. Max allowed SIP for this row (given current Lumpsum in this row + other rows)
                        const remTargetForSipFV = Math.max(0, selfFundNeedFV - currHoldingFV - otherRowsHoldingFV - otherRowsSipFV);
                        const sipCapForRow = calcSipReq(remTargetForSipFV, n, GoalExpRet, AI_goal);
                        const effSipLimit = Math.min(limS_pool, sipCapForRow);
                        const sipIsBinding = sipCapForRow <= limS_pool;

                        return (
                          <div key={row.id} className={styles.taggedRow}>

                            {/* Asset selector */}
                            <select className={styles.rowSelect} value={row.name}
                              onChange={e => {
                                const name = e.target.value;
                                if (!name) {
                                  if (isInit) {
                                    setSavedGoalsList(prev => prev.map(g =>
                                      g.id !== goal.id ? g : {
                                        ...g,
                                        additional_investment: [{ id: Date.now(), name: '', CurrentAvailable: '', SIPAmt: '' }],
                                      }
                                    ));
                                  } else {
                                    handleSelectInvestment(goal.id, row.id, '', '', '');
                                  }
                                  return;
                                }

                                const otherH_sel = (goal.additional_investment || [])
                                  .filter(r => r.id !== row.id && r.name === name)
                                  .reduce((sum, r) => sum + (Number(String(r.CurrentAvailable).replace(/,/g, '')) || 0), 0);
                                const otherS_sel = (goal.additional_investment || [])
                                  .filter(r => r.id !== row.id && r.name === name)
                                  .reduce((sum, r) => sum + (Number(String(r.SIPAmt).replace(/,/g, '')) || 0), 0);

                                const poolAvailH = Math.max(0, (pool.holdings[name] || 0) - otherH_sel);
                                const poolAvailS = Math.max(0, (pool.sips[name] || 0) - otherS_sel);

                                // Target FV for this row
                                const remTargetFV = Math.max(0, selfFundNeedFV - otherRowsHoldingFV - otherRowsSipFV);

                                // 1. Auto-fill Lumpsum first up to required lumpsum
                                const reqLumpsum = calcHoldingReq(remTargetFV, n, GoalExpRet);
                                const hVal = Math.min(poolAvailH, reqLumpsum);
                                const hStr = hVal > 0 ? String(hVal) : '';

                                // 2. Check if goal is achieved by Lumpsum
                                const hFV = calcHoldingFV(hVal, n, GoalExpRet);
                                const remTargetAfterHFV = Math.max(0, remTargetFV - hFV);

                                let sStr = '';
                                if (remTargetAfterHFV > 0) {
                                  // Goal not achieved by Lumpsum alone: fill remaining required SIP
                                  const reqSip = calcSipReq(remTargetAfterHFV, n, GoalExpRet, AI_goal);
                                  const sVal = Math.min(poolAvailS, reqSip);
                                  sStr = sVal > 0 ? String(sVal) : '';
                                }

                                if (isInit) {
                                  setSavedGoalsList(prev => prev.map(g =>
                                    g.id !== goal.id ? g : {
                                      ...g,
                                      additional_investment: [{ id: Date.now(), name, CurrentAvailable: hStr, SIPAmt: sStr }],
                                    }
                                  ));
                                } else {
                                  handleSelectInvestment(goal.id, row.id, name, hStr, sStr);
                                }
                              }}>
                              <option value="">— Select —</option>
                              {getSelectOptions().map(opt => {
                                const otherH_opt = (goal.additional_investment || [])
                                  .filter(r => r.id !== row.id && r.name === opt)
                                  .reduce((sum, r) => sum + (Number(String(r.CurrentAvailable).replace(/,/g, '')) || 0), 0);
                                const otherS_opt = (goal.additional_investment || [])
                                  .filter(r => r.id !== row.id && r.name === opt)
                                  .reduce((sum, r) => sum + (Number(String(r.SIPAmt).replace(/,/g, '')) || 0), 0);
                                const hv = Math.max(0, (pool.holdings[opt] || 0) - otherH_opt);
                                const sv = Math.max(0, (pool.sips[opt] || 0) - otherS_opt);
                                const dn = opt === 'FD/RD' ? 'FD / RD'
                                  : (opt === 'Other Liquid' || opt === 'Other Non Ret.') ? 'Other Non Ret.' : opt;
                                return (
                                  <option key={opt} value={opt}>
                                    {`${dn} — ₹${new Intl.NumberFormat('en-IN').format(hv)} holding | SIP ₹${new Intl.NumberFormat('en-IN').format(sv)}/mo`}
                                  </option>
                                );
                              })}
                            </select>
                              {/* Holding input */}
                                  <input type="text" className={styles.rowInputHolding}
                                    placeholder={effHoldingLimit === 0 ? "0" : "Partial or full amount"}
                                    disabled={goal.saved || !row.name || (effHoldingLimit === 0 && !row.CurrentAvailable)}
                                    value={row.CurrentAvailable
                                      ? new Intl.NumberFormat('en-IN').format(Number(row.CurrentAvailable)) : ''}
                                    onChange={e => {
                                      const rawVal = e.target.value.replace(/[^0-9]/g, '');
                                      if (isInit) {
                                        let v = rawVal;
                                        if (row.name && Number(v) > effHoldingLimit) v = String(effHoldingLimit);
                                        setSavedGoalsList(prev => prev.map(g =>
                                          g.id !== goal.id ? g : {
                                            ...g,
                                            additional_investment: [{ id: Date.now(), name: '', CurrentAvailable: v, SIPAmt: '' }],
                                          }
                                        ));
                                      } else {
                                        handleUpdateTaggedInvestment(goal.id, row.id, 'CurrentAvailable', rawVal, effHoldingLimit);
                                      }
                                    }} />

                            {/* SIP input */}
                            <input type="text" className={styles.rowInputSip}
                              placeholder={effSipLimit === 0 ? "0" : "SIP/mo (₹)"}
                              disabled={goal.saved || !row.name || (effSipLimit === 0 && !row.SIPAmt)}
                              value={row.SIPAmt
                                ? new Intl.NumberFormat('en-IN').format(Number(row.SIPAmt)) : ''}
                              onChange={e => {
                                const rawVal = e.target.value.replace(/[^0-9]/g, '');
                                if (isInit) {
                                  let v = rawVal;
                                  if (row.name && Number(v) > effSipLimit) v = String(effSipLimit);
                                  setSavedGoalsList(prev => prev.map(g =>
                                    g.id !== goal.id ? g : {
                                      ...g,
                                      additional_investment: [{ id: Date.now(), name: '', CurrentAvailable: '', SIPAmt: v }],
                                    }
                                  ));
                                } else {
                                  handleUpdateTaggedInvestment(goal.id, row.id, 'SIPAmt', rawVal, effSipLimit);
                                }
                              }} />

                            {!isInit && (
                              <button className={styles.rowDeleteBtn}
                                onClick={() => handleDeleteTaggedInvestment(goal.id, row.id)}>
                                ✕
                              </button>
                            )}
                          </div>
                        );
                      })}

                      {(goal.additional_investment || []).filter(r => r.name).map((row, idx) => {
                        const pool = goalAvailablePools[goal.id] || { holdings: {}, sips: {} };
                        const hVal = pool.holdings[row.name] || 0;
                        const sVal = pool.sips[row.name] || 0;
                        const bal = Math.max(0, hVal - Number(row.CurrentAvailable || 0));
                        return (
                          <div key={idx} className={styles.fundedTaggedInfoRow}>
                            <span>
                              Available: <strong>₹{new Intl.NumberFormat('en-IN').format(hVal)} holding</strong>
                              {sVal > 0 && <span> | SIP ₹{new Intl.NumberFormat('en-IN').format(sVal)}/mo</span>}
                            </span>
                            <span style={{ color: '#2e7d32', fontWeight: 600 }}>
                              Balance for next goal: ₹{new Intl.NumberFormat('en-IN').format(bal)}
                            </span>
                          </div>
                        );
                      })}

                      <div className={styles.taggedFooterRow}>
                        <button className={styles.rowAddBtn}
                          onClick={() => handleAddTaggedInvestment(goal.id)}>
                          + Add Investment
                        </button>
                        <div className={styles.stepUpFieldGroup}>
                          <label className={styles.stepUpFieldLabel}>
                            Step-Up SIP % / year <span className={styles.stepUpBadge}>Step-Up</span>
                          </label>
                          <select className={styles.stepUpCompactSelect} disabled={goal.saved}
                            value={goal.GrowthRate}
                            onChange={e => {
                              handleDraftChange(goal.id, 'GrowthRate', e.target.value);
                            }}>
                            {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15].map(v => (
                              <option key={v} value={String(v)}>{v}%</option>
                            ))}
                          </select>
                          {Number(goal.GrowthRate || 0) > annualIncreaseCap && (
                            <div style={{ color: '#dc2626', fontSize: '0.75rem', marginTop: '4px', fontWeight: '600', lineHeight: '1.3' }}>
                              Step-up rate cannot exceed Annual Increase in Existing Investments ({annualIncreaseCap}%)
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                  <div className={styles.goalFulfilledBox}>
                    Goal <strong>Achieved</strong>
                  </div>
                </>
              ) : (
                <>
                  {GoalRequiredValue > 0 && (
                    <div className={styles.additionalRequiredBox}>
                      ⚠️&nbsp;<span style={{ marginLeft: '4px' }}>
                        <strong>Additional Investment Required</strong> —&nbsp;
                        {(mappedToGoal > 0 || InvestmentTaggedFV > 0 || MonthlyInvestmentFV > 0)
                          ? <>After investments + Capital Receipt, a shortfall of&nbsp;</>
                          : <>Shortfall of&nbsp;</>
                        }
                        <strong>{formatIndianValue(GoalNetReq)}</strong> remains against target of&nbsp;
                        <strong>{formatIndianValue(GoalRequiredValue)}</strong>.
                        Please add investments below.
                      </span>
                    </div>
                  )}

                  {goal.saved ? (
                    <div className={styles.savedMessage}>
                      {goal.additional_investment?.length > 0 ? (
                        <div className={styles.savedTaggedList}>
                          <div className={styles.savedTaggedTitle}>Tagged Investments:</div>
                          {goal.additional_investment.map((row, idx) => (
                            <div key={idx} className={styles.savedTaggedRow}>
                              <span>• {row.name || 'Unspecified'}: </span>
                              <strong>
                                Holding: ₹{row.CurrentAvailable
                                  ? Number(row.CurrentAvailable).toLocaleString('en-IN') : '0'}
                              </strong>
                              {row.SIPAmt && (
                                <span>&nbsp;|&nbsp;
                                  <strong>SIP: ₹{Number(row.SIPAmt).toLocaleString('en-IN')}/mo</strong>
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : 'No investments tagged — click Edit to modify.'}
                    </div>
                  ) : (
                    goal.GoalAmtReq && String(goal.GoalAmtReq).trim() !== '' && (
                      <div className={styles.taggedSection}>
                        <div className={styles.taggedTitle}>ADD INVESTMENTS TOWARDS THIS GOAL</div>
                        <div className={styles.taggedHeaderRow}>
                          <span>INVESTMENT (LIQUID)</span>
                          <span>ALLOCATED HOLDING (₹)</span>
                          <span>SIP / MO (₹)</span>
                          <span />
                        </div>

                        {((goal.additional_investment || []).length === 0
                          ? [{ id: 'init-empty', name: '', CurrentAvailable: '', SIPAmt: '' }]
                          : goal.additional_investment
                        ).map(row => {
                          const isInit = row.id === 'init-empty';
                          const pool = goalAvailablePools[goal.id] || { holdings: {}, sips: {} };
                          const AI_goal = (Number(goal.GrowthRate) || 0) / 100;

                          // Net FV requirement to be self-funded for this goal (excluding CR / carried surplus)
                          const selfFundNeedFV = Math.max(0, GoalRequiredValue - mappedToGoal);

                          // Pool availability limits for row.name
                          const otherH_asset = (goal.additional_investment || [])
                            .filter(r => r.id !== row.id && r.name === row.name)
                            .reduce((sum, r) => sum + (Number(String(r.CurrentAvailable).replace(/,/g, '')) || 0), 0);
                          const otherS_asset = (goal.additional_investment || [])
                            .filter(r => r.id !== row.id && r.name === row.name)
                            .reduce((sum, r) => sum + (Number(String(r.SIPAmt).replace(/,/g, '')) || 0), 0);

                          const limH_pool = row.name ? Math.max(0, (pool.holdings[row.name] || 0) - otherH_asset) : 0;
                          const limS_pool = row.name ? Math.max(0, (pool.sips[row.name] || 0) - otherS_asset) : 0;

                          // Other rows' holdings FV and SIPs FV
                          const otherRowsHoldingFV = (goal.additional_investment || [])
                            .filter(r => r.id !== row.id)
                            .reduce((sum, r) => sum + calcHoldingFV(r.CurrentAvailable, n, GoalExpRet), 0);
                          const otherRowsSipFV = (goal.additional_investment || [])
                            .filter(r => r.id !== row.id)
                            .reduce((sum, r) => sum + calcSipFV(r.SIPAmt, n, GoalExpRet, AI_goal), 0);

                          // Current row's entered values
                          const currHoldingVal = Number(String(row.CurrentAvailable || 0).replace(/,/g, '')) || 0;
                          const currSipVal = Number(String(row.SIPAmt || 0).replace(/,/g, '')) || 0;

                          const currHoldingFV = calcHoldingFV(currHoldingVal, n, GoalExpRet);
                          const currSipFV = calcSipFV(currSipVal, n, GoalExpRet, AI_goal);

                          // 1. Max allowed Lumpsum for this row (given current SIP in this row + other rows)
                          const remTargetForLumpsumFV = Math.max(0, selfFundNeedFV - currSipFV - otherRowsSipFV - otherRowsHoldingFV);
                          const lumpsumCapForRow = calcHoldingReq(remTargetForLumpsumFV, n, GoalExpRet);
                          const effHoldingLimit = Math.min(limH_pool, lumpsumCapForRow);
                          const lumpsumIsBinding = lumpsumCapForRow <= limH_pool;

                          // 2. Max allowed SIP for this row (given current Lumpsum in this row + other rows)
                          const remTargetForSipFV = Math.max(0, selfFundNeedFV - currHoldingFV - otherRowsHoldingFV - otherRowsSipFV);
                          const sipCapForRow = calcSipReq(remTargetForSipFV, n, GoalExpRet, AI_goal);
                          const effSipLimit = Math.min(limS_pool, sipCapForRow);
                          const sipIsBinding = sipCapForRow <= limS_pool;

                          return (
                            <div key={row.id} className={styles.taggedRow}>

                              {/* Asset selector */}
                              <select className={styles.rowSelect} value={row.name}
                                onChange={e => {
                                  const name = e.target.value;
                                  if (!name) {
                                    if (isInit) {
                                      setSavedGoalsList(prev => prev.map(g =>
                                        g.id !== goal.id ? g : {
                                          ...g,
                                          additional_investment: [{ id: Date.now(), name: '', CurrentAvailable: '', SIPAmt: '' }],
                                        }
                                      ));
                                    } else {
                                      handleSelectInvestment(goal.id, row.id, '', '', '');
                                    }
                                    return;
                                  }

                                  const otherH_sel = (goal.additional_investment || [])
                                    .filter(r => r.id !== row.id && r.name === name)
                                    .reduce((sum, r) => sum + (Number(String(r.CurrentAvailable).replace(/,/g, '')) || 0), 0);
                                  const otherS_sel = (goal.additional_investment || [])
                                    .filter(r => r.id !== row.id && r.name === name)
                                    .reduce((sum, r) => sum + (Number(String(r.SIPAmt).replace(/,/g, '')) || 0), 0);

                                  const poolAvailH = Math.max(0, (pool.holdings[name] || 0) - otherH_sel);
                                  const poolAvailS = Math.max(0, (pool.sips[name] || 0) - otherS_sel);

                                  // Target FV for this row
                                  const remTargetFV = Math.max(0, selfFundNeedFV - otherRowsHoldingFV - otherRowsSipFV);

                                  // 1. Auto-fill Lumpsum first up to required lumpsum
                                  const reqLumpsum = calcHoldingReq(remTargetFV, n, GoalExpRet);
                                  const hVal = Math.min(poolAvailH, reqLumpsum);
                                  const hStr = hVal > 0 ? String(hVal) : '';

                                  // 2. Check if goal is achieved by Lumpsum
                                  const hFV = calcHoldingFV(hVal, n, GoalExpRet);
                                  const remTargetAfterHFV = Math.max(0, remTargetFV - hFV);

                                        let sStr = '';
                                        if (remTargetAfterHFV > 0) {
                                          const reqSip = calcSipReq(remTargetAfterHFV, n, GoalExpRet, AI_goal);
                                          const sVal = Math.min(poolAvailS, reqSip);
                                          sStr = sVal > 0 ? String(sVal) : '';
                                        }

                                        if (isInit) {
                                          setSavedGoalsList(prev => prev.map(g =>
                                            g.id !== goal.id ? g : {
                                              ...g,
                                              additional_investment: [{ id: Date.now(), name, CurrentAvailable: hStr, SIPAmt: sStr }],
                                            }
                                          ));
                                        } else {
                                          handleSelectInvestment(goal.id, row.id, name, hStr, sStr);
                                        }
                                      }}>
                                      <option value="">— Select —</option>
                                      {getSelectOptions().map(opt => {
                                        const otherH_opt = (goal.additional_investment || [])
                                          .filter(r => r.id !== row.id && r.name === opt)
                                          .reduce((sum, r) => sum + (Number(String(r.CurrentAvailable).replace(/,/g, '')) || 0), 0);
                                        const otherS_opt = (goal.additional_investment || [])
                                          .filter(r => r.id !== row.id && r.name === opt)
                                          .reduce((sum, r) => sum + (Number(String(r.SIPAmt).replace(/,/g, '')) || 0), 0);
                                        const hv = Math.max(0, (pool.holdings[opt] || 0) - otherH_opt);
                                        const sv = Math.max(0, (pool.sips[opt] || 0) - otherS_opt);
                                        const dn = opt === 'FD/RD' ? 'FD / RD'
                                          : (opt === 'Other Liquid' || opt === 'Other Non Ret.') ? 'Other Non Ret.' : opt;
                                        return (
                                          <option key={opt} value={opt}>
                                            {`${dn} — ₹${new Intl.NumberFormat('en-IN').format(hv)} holding | SIP ₹${new Intl.NumberFormat('en-IN').format(sv)}/mo`}
                                          </option>
                                        );
                                      })}
                                    </select>

                              {/* Holding input */}
                              <input type="text" className={styles.rowInputHolding}
                                placeholder={effHoldingLimit === 0 ? "0" : "Partial or full amount"}
                                disabled={goal.saved || !row.name || (effHoldingLimit === 0 && !row.CurrentAvailable)}
                                value={row.CurrentAvailable
                                  ? new Intl.NumberFormat('en-IN').format(Number(row.CurrentAvailable)) : ''}
                                onChange={e => {
                                  const rawVal = e.target.value.replace(/[^0-9]/g, '');
                                  if (isInit) {
                                    let v = rawVal;
                                    if (row.name && Number(v) > effHoldingLimit) v = String(effHoldingLimit);
                                    setSavedGoalsList(prev => prev.map(g =>
                                      g.id !== goal.id ? g : {
                                        ...g,
                                        additional_investment: [{ id: Date.now(), name: '', CurrentAvailable: v, SIPAmt: '' }],
                                      }
                                    ));
                                  } else {
                                    handleUpdateTaggedInvestment(goal.id, row.id, 'CurrentAvailable', rawVal, effHoldingLimit);
                                  }
                                }} />

                              {/* SIP input */}
                              <input type="text" className={styles.rowInputSip}
                                placeholder={effSipLimit === 0 ? "0" : "SIP/mo (₹)"}
                                disabled={goal.saved || !row.name || (effSipLimit === 0 && !row.SIPAmt)}
                                value={row.SIPAmt
                                  ? new Intl.NumberFormat('en-IN').format(Number(row.SIPAmt)) : ''}
                                onChange={e => {
                                  const rawVal = e.target.value.replace(/[^0-9]/g, '');
                                  if (isInit) {
                                    let v = rawVal;
                                    if (row.name && Number(v) > effSipLimit) v = String(effSipLimit);
                                    setSavedGoalsList(prev => prev.map(g =>
                                      g.id !== goal.id ? g : {
                                        ...g,
                                        additional_investment: [{ id: Date.now(), name: '', CurrentAvailable: '', SIPAmt: v }],
                                      }
                                    ));
                                  } else {
                                    handleUpdateTaggedInvestment(goal.id, row.id, 'SIPAmt', rawVal, effSipLimit);
                                  }
                                }} />

                              {!isInit && (
                                <button className={styles.rowDeleteBtn}
                                  onClick={() => handleDeleteTaggedInvestment(goal.id, row.id)}>
                                  ✕
                                </button>
                              )}
                            </div>
                          );
                        })}

                        <div className={styles.taggedFooterRow}>
                          <button className={styles.rowAddBtn}
                            onClick={() => handleAddTaggedInvestment(goal.id)}>
                            + Add Investment
                          </button>
                          <div className={styles.stepUpFieldGroup}>
                            <label className={styles.stepUpFieldLabel}>
                              Step-Up SIP % / year <span className={styles.stepUpBadge}>Step-Up</span>
                            </label>
                            <select className={styles.stepUpCompactSelect} disabled={goal.saved}
                              value={goal.GrowthRate}
                              onChange={e => {
                                handleDraftChange(goal.id, 'GrowthRate', e.target.value);
                              }}>
                              {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15].map(v => (
                                <option key={v} value={String(v)}>{v}%</option>
                              ))}
                            </select>
                            {Number(goal.GrowthRate || 0) > annualIncreaseCap && (
                              <div style={{ color: '#dc2626', fontSize: '0.75rem', marginTop: '4px', fontWeight: '600', lineHeight: '1.3' }}>
                                Step-up rate cannot exceed Annual Increase in Existing Investments ({annualIncreaseCap}%)
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    )
                  )}

                        {GoalRequiredValue > 0 && (
                          <div className={styles.shortfallSummaryBox}>
                            Shortfall:&nbsp;<strong>₹{new Intl.NumberFormat('en-IN').format(Math.ceil(GoalNetReq))}</strong>
                            &nbsp;| Indicative SIP:&nbsp;
                            <strong>₹{new Intl.NumberFormat('en-IN').format(Math.ceil(indicativeSip))}/mo</strong>
                            &nbsp;| Lumpsum:&nbsp;<strong>₹{new Intl.NumberFormat('en-IN').format(Math.ceil(indicativeLumpsum))}</strong>
                          </div>
                        )}
                      </>
                    )}
                  </>
                );
              })()}

              {!goal.saved && (
                <button className={styles.saveGoalBtn}
                  onClick={() => handleEditClick(goal.id, true)}>
                  ✓ Save Goal
                </button>
              )}

            </div>
          );
        })}

        <div className={styles.infoBox}>
          <strong>How calculations work:</strong> Allocate lumpsum holdings from your liquid
          investments to each goal. Remaining pool reduces for subsequent goals. Surplus from
          a funded goal cascades forward. The full plan with analysis appears in the PDF.
        </div>
      </div>

      <div className={styles.footer}>
        <button className={styles.backBtn} onClick={() => {
          // Persist current goals to localStorage so they survive Back navigation
          const raw = localStorage.getItem('clientInvestmentData');
          if (raw) {
            try {
              const d = JSON.parse(raw);
              d.goals = savedGoalsList.map(g => ({
                goal_type: g.GoalType,
                goal_name: ['education', 'marriage'].includes(g.typeId) ? g.ChildName : g.GoalName,
                child_name: ['education', 'marriage'].includes(g.typeId) ? g.ChildName : '',
                goal_year: Number(g.GoalYear),
                amount: Number(String(g.GoalAmtReq).replace(/,/g, '')),
                current_savings_or_age: Number(g.ChildAge),
                expected_inflation: g.expected_inflation,
                stepup_sip: g.GrowthRate,
                loan_amount: g.LoanFunding ? Number(g.LoanFunding) : 0,
                additional_investment: (g.additional_investment || []).filter(r => r.name).map(r => ({
                  name: r.name,
                  amount: r.CurrentAvailable ? Number(r.CurrentAvailable) : 0,
                  sip_amount: r.SIPAmt ? Number(r.SIPAmt) : 0,
                })),
              }));
              localStorage.setItem('clientInvestmentData', JSON.stringify(d));
            } catch (e) { console.error('Error saving goals on back', e); }
          }
          window.scrollTo({ top: 0, behavior: 'smooth' });
          navigate('/insurance-loans');
        }}
        >
          Back
        </button>
        <button className={styles.prepareBtn} onClick={handleSaveAndContinue} disabled={loading}>
          <DocumentIcon /> Prepare Plan →
        </button>
      </div>
    </div>
  );
}

export default GoalDetail;
